import { store } from "store/index";

const NOVA_API_URL = import.meta.env.VITE_NOVA_API_URL || "";
const NOVA_API_TOKEN = import.meta.env.VITE_NOVA_API_TOKEN || "";

function getNovaHeaders() {
  return {
    accept: "application/json",
    "Content-Type": "application/json",
    "X-API-Token": NOVA_API_TOKEN,
  };
}

function getUserId() {
  const user = store.getState().user;
  return user?.id != null ? String(user.id) : "";
}

function assertNovaConfig() {
  if (!NOVA_API_URL || !NOVA_API_TOKEN) {
    throw new Error("Nova chat is not configured. Please contact support.");
  }
}

function parseSSELine(line) {
  if (!line.startsWith("data: ")) return null;

  const data = line.slice(6).trim();
  if (!data || data === "[DONE]") {
    return data === "[DONE]" ? { done: true } : null;
  }

  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function processSSEBuffer(buffer, rawChunk) {
  const combined = buffer + rawChunk;
  const lines = combined.split("\n");
  const remaining = lines.pop() || "";
  const events = [];

  for (const line of lines) {
    const parsed = parseSSELine(line);
    if (parsed) events.push(parsed);
  }

  return { events, buffer: remaining };
}

export async function streamChatReply({
  question,
  examType,
  threadId,
  signal,
  onToken,
  onError,
  onDone,
  onThreadId,
  onChatsLeft,
  onStudyPlan,
  userType,
}) {
  assertNovaConfig();

  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be logged in to use Nova chat.");
  }

  const response = await fetch(`${NOVA_API_URL}/query/stream`, {
    method: "POST",
    headers: getNovaHeaders(),
    body: JSON.stringify({
      user_id: userId,
      question,
      exam_type: examType,
      thread_id: threadId || null,
      user_type: userType
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Chat request failed (${response.status}). Please try again.`);
  }

  if (!response.body) {
    throw new Error("Streaming is not supported in this browser.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const rawChunk = decoder.decode(value, { stream: true });
    const { events, buffer: nextBuffer } = processSSEBuffer(buffer, rawChunk);
    buffer = nextBuffer;

    for (const event of events) {
      if (event.done) {
        onDone?.();
        continue;
      }
      if (event.thread_id) {
        onThreadId?.(event.thread_id);
      }
      if (event.chats_left != null) {
        onChatsLeft?.(event.chats_left);
      }
      if (event.is_study_plan) {
        onStudyPlan?.();
      }
      if (event.token) {
        onToken?.(event.token);
      }
      if (event.error) {
        onError?.(event.error);
      }
    }
  }

  onDone?.();
}

export async function clearChatHistory(examType) {
  assertNovaConfig();

  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be logged in to clear chat history.");
  }

  const response = await fetch(
    `${NOVA_API_URL}/clear/history/${encodeURIComponent(userId)}?exam_type=${encodeURIComponent(examType)}`,
    {
      method: "DELETE",
      headers: getNovaHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to clear chat history (${response.status}).`);
  }

  return response;
}

export async function fetchChatHistory() {
  const userId = getUserId();
  if (!userId) {
    return null;
  }

  const BASE_URL = import.meta.env.VITE_API_URL || "";
  const token = store.getState().token;

  try {
    const response = await fetch(
      `${BASE_URL}/api/v1/nova/chat/${encodeURIComponent(userId)}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    if (!text || !text.trim()) {
      return null;
    }

    const data = JSON.parse(text);
    if (!data || typeof data !== "object") {
      return null;
    }

    return data;
  } catch (error) {
    console.error("Failed to fetch chat history:", error);
    return null;
  }
}

export async function startNewChat(examType) {
  assertNovaConfig();

  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be logged in to start a new chat.");
  }

  const BASE_URL = import.meta.env.VITE_API_URL || "";
  const token = store.getState().token;

  const response = await fetch(`${BASE_URL}/api/v1/nova/chat/new`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ exam_type: examType }),
  });

  if (!response.ok) {
    throw new Error(`Failed to start a new chat (${response.status}).`);
  }

  try {
    const text = await response.text();
    if (!text || !text.trim()) {
      return { thread_id: null };
    }
    const data = JSON.parse(text);
    return {
      thread_id: data?.thread_id ?? null,
      ...data,
    };
  } catch {
    return { thread_id: null };
  }
}

export async function submitMessageLike(messageId, liked) {
  return Promise.resolve({ messageId, liked, success: true });
}

export async function submitMessageDislike(messageId, disliked) {
  return Promise.resolve({ messageId, disliked, success: true });
}

export async function submitMessageReport({ threadId, messageIndex, comment }) {
  assertNovaConfig();

  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be logged in to submit a report.");
  }
  if (!threadId) {
    throw new Error("Unable to report this message. Please try again later.");
  }

  const response = await fetch(`${NOVA_API_URL}/report`, {
    method: "POST",
    headers: getNovaHeaders(),
    body: JSON.stringify({
      user_id: userId,
      thread_id: threadId,
      report_type: "message",
      message_index: messageIndex,
      comment,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit report (${response.status}). Please try again.`);
  }

  try {
    const text = await response.text();
    if (!text || !text.trim()) {
      return { success: true };
    }
    return { success: true, ...JSON.parse(text) };
  } catch {
    return { success: true };
  }
}

export async function submitChatFeedback({ threadId, rating, comment }) {
  assertNovaConfig();

  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be logged in to submit feedback.");
  }
  if (!threadId) {
    throw new Error("Unable to submit feedback. Please try again later.");
  }

  const response = await fetch(`${NOVA_API_URL}/feedback`, {
    method: "POST",
    headers: getNovaHeaders(),
    body: JSON.stringify({
      user_id: userId,
      thread_id: threadId,
      rating: rating == null ? null : Number(rating),
      comment: comment || "",
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit feedback (${response.status}). Please try again.`);
  }

  try {
    const text = await response.text();
    if (!text || !text.trim()) {
      return { success: true };
    }
    return { success: true, ...JSON.parse(text) };
  } catch {
    return { success: true };
  }
}
